import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Store reservations per restaurant: { [restaurantSlug]: reservationState }
  reservations: {},
  currentRestaurantSlug: null,
};

const reservationSlice = createSlice({
  name: 'reservation',
  initialState,
  reducers: {
    // Initialize reservation for a restaurant
    initializeReservation: (state, action) => {
      const { restaurantSlug } = action.payload;
      if (restaurantSlug && !state.reservations[restaurantSlug]) {
        state.reservations[restaurantSlug] = {
          guests: 2,
          selectedDate: '',
          selectedTime: '',
          selectedTimeAdjustment: '2h',
          selectedServiceAreaId: '',
          selectedMenuPresetId: '',
          specialItemQty: {},
        };
        state.currentRestaurantSlug = restaurantSlug;
      }
    },
    
    // Set current restaurant
    setCurrentRestaurant: (state, action) => {
      const { restaurantSlug } = action.payload;
      state.currentRestaurantSlug = restaurantSlug;
      if (restaurantSlug && !state.reservations[restaurantSlug]) {
        state.reservations[restaurantSlug] = {
          guests: 2,
          selectedDate: '',
          selectedTime: '',
          selectedTimeAdjustment: '2h',
          selectedServiceAreaId: '',
          selectedMenuPresetId: '',
          specialItemQty: {},
        };
      }
    },
    
    // Update guests
    setGuests: (state, action) => {
      const { restaurantSlug, guests } = action.payload;
      if (restaurantSlug && state.reservations[restaurantSlug]) {
        state.reservations[restaurantSlug].guests = guests;
      }
    },
    
    // Update selected date
    setSelectedDate: (state, action) => {
      const { restaurantSlug, date } = action.payload;
      if (restaurantSlug && state.reservations[restaurantSlug]) {
        state.reservations[restaurantSlug].selectedDate = date;
      }
    },
    
    // Update selected time
    setSelectedTime: (state, action) => {
      const { restaurantSlug, time } = action.payload;
      if (restaurantSlug && state.reservations[restaurantSlug]) {
        state.reservations[restaurantSlug].selectedTime = time;
      }
    },
    
    // Update time adjustment
    setTimeAdjustment: (state, action) => {
      const { restaurantSlug, adjustment } = action.payload;
      if (restaurantSlug && state.reservations[restaurantSlug]) {
        state.reservations[restaurantSlug].selectedTimeAdjustment = adjustment;
      }
    },
    
    // Update service area
    setServiceArea: (state, action) => {
      const { restaurantSlug, serviceAreaId } = action.payload;
      if (restaurantSlug && state.reservations[restaurantSlug]) {
        state.reservations[restaurantSlug].selectedServiceAreaId = serviceAreaId;
      }
    },
    
    // Update menu preset
    setMenuPreset: (state, action) => {
      const { restaurantSlug, menuPresetId } = action.payload;
      if (restaurantSlug && state.reservations[restaurantSlug]) {
        state.reservations[restaurantSlug].selectedMenuPresetId = menuPresetId;
      }
    },
    
    // Update special item quantity
    setSpecialItemQty: (state, action) => {
      const { restaurantSlug, itemId, quantity } = action.payload;
      if (restaurantSlug && state.reservations[restaurantSlug]) {
        const qty = state.reservations[restaurantSlug].specialItemQty || {};
        if (quantity <= 0) {
          delete qty[itemId];
        } else {
          qty[itemId] = quantity;
        }
        state.reservations[restaurantSlug].specialItemQty = { ...qty };
      }
    },
    
    // Increment special item quantity
    incrementSpecialItem: (state, action) => {
      const { restaurantSlug, itemId } = action.payload;
      if (restaurantSlug && state.reservations[restaurantSlug]) {
        const qty = state.reservations[restaurantSlug].specialItemQty || {};
        qty[itemId] = (qty[itemId] || 0) + 1;
        state.reservations[restaurantSlug].specialItemQty = { ...qty };
      }
    },
    
    // Decrement special item quantity
    decrementSpecialItem: (state, action) => {
      const { restaurantSlug, itemId } = action.payload;
      if (restaurantSlug && state.reservations[restaurantSlug]) {
        const qty = state.reservations[restaurantSlug].specialItemQty || {};
        const currentQty = qty[itemId] || 0;
        if (currentQty <= 1) {
          delete qty[itemId];
        } else {
          qty[itemId] = currentQty - 1;
        }
        state.reservations[restaurantSlug].specialItemQty = { ...qty };
      }
    },
    
    // Clear reservation
    clearReservation: (state, action) => {
      const { restaurantSlug } = action.payload;
      if (restaurantSlug && state.reservations[restaurantSlug]) {
        state.reservations[restaurantSlug] = {
          guests: 2,
          selectedDate: '',
          selectedTime: '',
          selectedTimeAdjustment: '2h',
          selectedServiceAreaId: '',
          selectedMenuPresetId: '',
          specialItemQty: {},
        };
      }
    },
  },
});

export const {
  initializeReservation,
  setCurrentRestaurant,
  setGuests,
  setSelectedDate,
  setSelectedTime,
  setTimeAdjustment,
  setServiceArea,
  setMenuPreset,
  setSpecialItemQty,
  incrementSpecialItem,
  decrementSpecialItem,
  clearReservation,
} = reservationSlice.actions;

// Selectors
export const selectReservation = (state, restaurantSlug) => {
  if (!restaurantSlug) {
    return {
      guests: 2,
      selectedDate: '',
      selectedTime: '',
      selectedTimeAdjustment: '2h',
      selectedServiceAreaId: '',
      selectedMenuPresetId: '',
      specialItemQty: {},
    };
  }
  return state.reservation.reservations[restaurantSlug] || {
    guests: 2,
    selectedDate: '',
    selectedTime: '',
    selectedTimeAdjustment: '2h',
    selectedServiceAreaId: '',
    selectedMenuPresetId: '',
    specialItemQty: {},
  };
};

export const selectSpecialItemQty = (state, restaurantSlug, itemId) => {
  const reservation = selectReservation(state, restaurantSlug);
  return reservation.specialItemQty[itemId] || 0;
};

export default reservationSlice.reducer;

